import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import { ApiResponse } from '@/app/common/types/common';
import customFetchBase from '@/app/common/utils/customFetchBase';
import {
  CountKeywordHistoryData,
  DeleteGscHistoryRequest,
  GscDateChartData,
  GscDateTotalsData,
  GetDateTotalsRequest,
  ExportTableRequest,
  GetAuthUrlData,
  GetAuthUrlRequest,
  GetKeywordHistoryRequest,
  GscAccountId,
  GscHistoryData,
  GscHostName,
  GscHostNameId,
  RefreshHostNamesData,
  RefreshGscHistoryData,
  RefreshUserGscHistoryRequest
} from '@/app/KeywordResearch/types/gscApiTypes';

const gscApi = createApi({
  reducerPath: 'gscApi',
  tagTypes: ['userGscHistory', 'userGscHostNames'],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    getAuthUrl: build.mutation<
      ApiResponse<GetAuthUrlData>,
      GetAuthUrlRequest
    >({
      query: (body: GetAuthUrlRequest) => ({
        url: '/keyword_gsc/v1/google_oauth2/get_auth_url',
        method: 'POST',
        body
      }),
    }),
    getUserGscHistory: build.query<ApiResponse<GscHistoryData>, void>({
      query: () => ({
        url: '/keyword_gsc/v1/search_console/user_history',
        method: 'POST'
      }),
      providesTags: ['userGscHistory']
    }),
    getHostNames: build.query<ApiResponse<GscHostName[]>, void>({
      query: () => ({
        url: '/keyword_gsc/v1/search_console/get_host_name_verify',
        method: 'POST'
      }),
      providesTags: ['userGscHostNames']
    }),
    refreshHostNames: build.mutation<
      RefreshHostNamesData,
      GscAccountId
    >({
      query: (account_id: GscAccountId) => ({
        url: '/keyword_gsc/v1/search_console/refresh_host_name',
        method: 'POST',
        body: { account_id }
      }),
      transformResponse: (response: ApiResponse<RefreshHostNamesData>) => {
        return response.data;
      }
    }),
    getDataFromSc: build.mutation<
      ApiResponse<RefreshGscHistoryData>,
      RefreshUserGscHistoryRequest
    >({
      query: (host_name_id: RefreshUserGscHistoryRequest) => ({
        url: '/keyword_gsc/v1/search_console/get_data_from_sc',
        method: 'POST',
        body: { host_name_id }
      }),
      invalidatesTags: ['userGscHistory']
    }),
    deleteGscHistory: build.mutation<
      ApiResponse<unknown>,
      DeleteGscHistoryRequest
    >({
      query: (body: DeleteGscHistoryRequest) => ({
        url: '/keyword_gsc/v1/search_console/delete_history',
        method: 'POST',
        body
      })
    }),
    countKeywordHistory: build.query<
      ApiResponse<CountKeywordHistoryData>,
      GscHostNameId
    >({
      query: (host_name_id: GscHostNameId) => ({
        url: '/keyword_gsc/v1/search_console/count_queries',
        method: 'POST',
        body: { host_name_id }
      })
    }),
    getDateChart: build.query<ApiResponse<GscDateChartData>, GscHostNameId>({
      query: (host_name_id: GscHostNameId) => ({
        url: '/keyword_gsc/v1/search_console/get_date_chart',
        method: 'POST',
        body: { host_name_id }
      })
    }),
    getDateTotals: build.query<
      ApiResponse<GscDateTotalsData>,
      GetDateTotalsRequest
    >({
      query: ({ host_name_id, start_date, end_date }) => ({
        url: '/keyword_gsc/v1/search_console/get_date_totals',
        method: 'POST',
        body: {
          host_name_id,
          ...(start_date && end_date ? { start_date, end_date } : {})
        }
      })
    }),
    getKeywordHistory: build.mutation<
      ApiResponse<unknown>,
      GetKeywordHistoryRequest
    >({
      query: (body: GetKeywordHistoryRequest) => ({
        url: '/keyword_gsc/v1/search_console/get_queries',
        method: 'POST',
        body
      })
    }),
    deleteAccount: build.mutation<ApiResponse<unknown>, GscAccountId>({
      query: (account_id: GscAccountId) => ({
        url: '/keyword_gsc/v1/search_console/revoke',
        method: 'POST',
        body: { account_id }
      })
    }),
    exportTable: build.mutation<ApiResponse<unknown>, ExportTableRequest>({
      query: (body: ExportTableRequest) => ({
        url: '/keyword_gsc/v1/search_console/export_data',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useGetAuthUrlMutation,
  useGetUserGscHistoryQuery,
  useGetHostNamesQuery,
  useLazyGetHostNamesQuery,
  useRefreshHostNamesMutation,
  useGetDataFromScMutation,
  useCountKeywordHistoryQuery,
  useGetDateChartQuery,
  useLazyGetDateChartQuery,
  useGetDateTotalsQuery,
  useLazyGetDateTotalsQuery,
  useDeleteGscHistoryMutation,
  useGetKeywordHistoryMutation,
  useDeleteAccountMutation,
  useExportTableMutation
} = gscApi;

export default gscApi;
